'use client'

import { ChevronRightIcon } from '@repo/ui/ChevronRightIcon'
import { formatDate } from '@repo/utils/time'
import { IconButton, SxProps } from '@mui/material'
import { Link } from 'react-router-dom'
import { pieArcClasses, PieChart } from '@mui/x-charts/PieChart'
import { styled } from '@mui/material/styles'
import { useEffect, useState } from 'react'
import { useMattersStore } from '@repo/store/useMattersStore'
import { useMatterWorkflowStore } from '@repo/store/useMatterWorkflowStore'
import { useMenuItemsStore } from '@repo/store/useMenuItemsStore'
import Carousel from 'react-material-ui-carousel'
import MUIPaper from '@mui/material/Paper'
import MUIStack from '@mui/material/Stack'
import MUITooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip'
import MUITypography from '@mui/material/Typography'
import PropTypes from 'prop-types'
import styles from './WorkflowProgressCard.module.scss'

export const WorkflowProgressCard = ({ sx }: WorkflowProgressCardProps) => {
  const { allowedMenuIds } = useMenuItemsStore()
  const { currentMatter } = useMattersStore()
  const { fetchMatterWorkflows, workflows, getWorkflowLink } = useMatterWorkflowStore()

  const { clientNo, clientMatterNo, matterNo } = currentMatter || {}
  const totalSlides = workflows.length ?? 0

  const [activeIndex, setActiveIndex] = useState(0)
  const [firstIncompleteTask, setFirstIncompleteTask] = useState<any>(null)
  const [pieData, setPieData] = useState<any[]>([])
  const [readWorkflows, setReadWorkflows] = useState<boolean>(false)

  useEffect(() => {
    if (clientMatterNo && clientNo) fetchMatterWorkflows({ clientMatterNo: clientMatterNo, clientNo })
  }, [clientMatterNo])

  useEffect(() => {
    if (workflows && workflows.length > 0) {
      const newPieData = workflows.map((workflow: any) => {
        return workflow.matterWorkflowWorkTypeTasks.map((task: any, i: number) => {
          const completed = Boolean(task.completedDate)
          return {
            // existing fields
            id: task.matterWorkflowWorkTypeTaskId,
            completedDate: task.completedDate,
            completed,
            value: 1,

            // new fields (adjust names as needed)
            label: task.taskDescription,
            workTypeTaskId: task.workTypeTaskId,
            positionIndex: task.positionIndex,
            workflowId: task.workflowId,
            matterWorkflowWorkTypeIdCount: task.matterWorkflowWorkTypeIdCount
          }
        })
      })
      setPieData(newPieData)

      const firstIncompleteTask = newPieData.flat().find((task: any) => !task.completed) || null
      setFirstIncompleteTask(firstIncompleteTask)

      // default to the first slide with an incomplete task
      let activeIndex = 0

      if (firstIncompleteTask?.workflowId) {
        activeIndex =
          workflows?.findIndex((workflow: any) => workflow?.workflowId === firstIncompleteTask.workflowId) || 0
      }

      setActiveIndex(activeIndex || 0)
    } else {
      setPieData([])
    }
  }, [workflows])

  useEffect(() => {
    if (allowedMenuIds.includes(3600)) {
      setReadWorkflows(true)
    }
  }, [allowedMenuIds])

  const handleCarouselChange = (now?: number, previous?: number) => {
    if (typeof now === 'number') {
      setActiveIndex(now)
    }
  }

  const handlePrev = () => {
    setActiveIndex((curr) => (curr === 0 ? totalSlides - 1 : curr - 1))
  }

  const handleNext = () => {
    setActiveIndex((curr) => (curr === totalSlides - 1 ? 0 : curr + 1))
  }

  const LinkTooltip = styled(({ className, ...props }: TooltipProps) => (
    <MUITooltip {...props} classes={{ popper: className }} />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: '#051C33',
      borderRadius: '4px',
      boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
      color: '#fff',
      fontSize: '0.875rem',
      lineHeight: '1.4',
      maxWidth: '200px',
      padding: '0.5rem 0.75rem 0.35rem',
      left: '1rem',
      bottom: '14px',
      position: 'relative',
      [`&:before`]: {
        content: '""',
        width: '0',
        height: '0',
        borderLeft: '5px solid transparent',
        borderRight: '5px solid transparent',
        borderBottom: '5px solid #051C33',
        position: 'absolute',
        top: '-4px',
        left: '50%'
      }
    }
  }))

  return (
    <div className={styles.WorkflowProgressCard} data-testid="WorkflowProgressCard">
      <MUIPaper
        sx={{
          width: '100%',
          padding: '0',
          background: 'white',
          borderRadius: 1,
          boxShadow: 'none',
          border: '1px solid #D8DCE5',
          position: 'relative',
          ...sx
        }}
      >
        <MUITypography
          sx={{
            padding: '1rem 1rem 1.45rem',
            height: '1rem'
          }}
          variant="h6"
        >
          Workflows
        </MUITypography>

        <Carousel
          animation="slide"
          autoPlay={false}
          className={styles.carousel}
          data-testid="carousel"
          index={activeIndex}
          onChange={handleCarouselChange}
          indicators={false}
          height={'14rem'}
          navButtonsAlwaysInvisible={true}
          navButtonsProps={{
            style: {
              backgroundColor: 'transparent',
              borderRadius: '4px',
              border: 'none',
              color: '#3d517b',
              cursor: 'pointer',
              fontSize: '0.875rem',
              margin: '0',
              padding: '0.25rem 1rem 0',
              transition: 'color 0.4s ease-in-out'
            }
          }}
          navButtonsWrapperProps={{
            style: {
              bottom: '1rem',
              height: 'auto',
              margin: '0.5rem 0',
              top: 'unset'
            }
          }}
          NextIcon={<ChevronRightIcon />}
          PrevIcon={<ChevronRightIcon sx={{ transform: 'rotate(180deg)' }} />}
          swipe={false}
        >
          {workflows && workflows.length > 0 ? (
            workflows.map((workflow: any, index: number) => {
              const tasksForChart = pieData[index] || []

              // Turn those tasks into a color array for the PieChart
              const colorArray = tasksForChart.map((task: any) => (task.completed ? '#3D517B' : '#DFE0E4'))

              const firstIncomplete = tasksForChart.find((task: any) => !task.completed)

              let linkUrl = ''
              if (firstIncomplete) {
                linkUrl = getWorkflowLink({
                  clientNo,
                  matterNo,
                  actionLinkedTaskId: firstIncomplete.workTypeTaskId,
                  taskPositionIndex: firstIncomplete.positionIndex,
                  workflowId: firstIncomplete.workflowId,
                  uniqueWorkflowCount: firstIncomplete.matterWorkflowWorkTypeIdCount
                })
              }

              const totalTasks = tasksForChart.length
              const completedTasks = tasksForChart.filter((task: any) => task.completed).length
              const percentage = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0

              return (
                <div className={styles.card} key={workflow.description} id="filledSlide" data-testid="filledSlide">
                  <div className={styles.pieChartWrapper}>
                    <PieChart
                      key={activeIndex === index ? `${index}-active` : `${index}-inactive`}
                      colors={colorArray}
                      series={[
                        {
                          data: tasksForChart.map((task: any) => ({
                            value: task.value,
                            label: task.label,
                            id: task.id,
                            completed: task.completed,
                            completedDate: task.completedDate
                          })),
                          innerRadius: 40,
                          outerRadius: 70,
                          paddingAngle: 2,
                          cornerRadius: 4,
                          startAngle: 0,
                          endAngle: 360,
                          cx: '125%',
                          cy: '50%',
                          highlightScope: { fade: 'global' },
                          faded: { innerRadius: 30, additionalRadius: -10 }
                        }
                      ]}
                      slotProps={{
                        legend: { hidden: true },
                        popper: {
                          sx: {
                            backgroundColor: 'transparent',
                            '& .MuiChartsTooltip-paper': {
                              backgroundColor: '#3D517B',
                              borderRadius: '4px',
                              boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
                              color: '#fff',
                              fontSize: '0.875rem',
                              lineHeight: '1.4',
                              maxWidth: '200px',
                              padding: '0.5rem 0.75rem 0.35rem',
                              '& .MuiTypography-root': {
                                color: '#fff'
                              },
                              '& .MuiChartsTooltip-markCell': {
                                display: 'none'
                              },
                              '& .MuiChartsTooltip-labelCell': {
                                color: '#fff',
                                fontWeight: '500'
                              },
                              '& .MuiChartsTooltip-valueCell': {
                                color: '#fff'
                              }
                            }
                          }
                        }
                      }}
                      slots={{
                        itemContent: (data: any) => {
                          const result = data.series.data.find((item: any) => item.index === data.itemData.dataIndex)

                          return (
                            <MUIStack
                              id={result?.label}
                              sx={{
                                backgroundColor: '#051C33',
                                borderRadius: '4px',
                                boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
                                color: '#fff',
                                fontSize: '0.875rem',
                                lineHeight: '1.4',
                                maxWidth: '200px',
                                padding: '0.5rem 0.75rem 0.35rem',
                                left: '1rem',
                                bottom: '14px',
                                position: 'relative',
                                [`&:before`]: {
                                  content: '""',
                                  width: '0',
                                  height: '0',
                                  borderRight: '5px solid #051C33',
                                  borderTop: '5px solid transparent',
                                  borderBottom: '5px solid transparent',
                                  position: 'absolute',
                                  top: '50%',
                                  left: '-4px'
                                }
                              }}
                            >
                              <MUITypography
                                variant="body2"
                                sx={{ color: '#fff', mb: 0, fontSize: '0.875rem', fontWeight: 500 }}
                              >
                                TASK
                              </MUITypography>
                              <MUITypography variant="body2" sx={{ color: '#fff', mb: 3 }}>
                                {`${result?.label}`}
                              </MUITypography>

                              <MUITypography
                                variant="body2"
                                sx={{ color: '#fff', mb: 0, fontSize: '0.875rem', fontWeight: 500 }}
                              >
                                COMPLETED
                              </MUITypography>
                              <MUITypography variant="body2" sx={{ color: '#fff', mb: 0 }}>
                                {`${result?.completed && result?.completedDate ? formatDate(result?.completedDate) : '-'}`}
                              </MUITypography>
                            </MUIStack>
                          )
                        }
                      }}
                      width={175}
                      height={150}
                      tooltip={{ trigger: 'item' }}
                      sx={{
                        [`& .${pieArcClasses.root}`]: {
                          cursor: 'pointer'
                        }
                      }}
                    />
                    <MUITypography
                      variant="h6"
                      data-testid="dataValueLabel"
                      sx={{
                        color: '#3D517B',
                        fontSize: '1.5rem',
                        fontWeight: '500',
                        left: '54%',
                        margin: '0 auto',
                        position: 'absolute',
                        top: '50%',
                        transform: 'translate(-50%, -50%)'
                      }}
                    >
                      {percentage}%
                    </MUITypography>
                  </div>
                  <MUIStack sx={{ width: '100%' }}>
                    <MUITypography variant="body1" className={styles.description}>
                      {workflow.description}
                    </MUITypography>

                    {linkUrl ? (
                      <LinkTooltip
                        className={styles.tooltip}
                        disableFocusListener
                        title={
                          !readWorkflows
                            ? `You are not allowed to view this task`
                            : firstIncompleteTask
                              ? `NEXT TASK: ${firstIncompleteTask.label}`
                              : 'No tasks available'
                        }
                        placement="bottom-end"
                      >
                        <Link
                          className={styles.link}
                          to={readWorkflows ? linkUrl : ''}
                          style={{
                            color: '#5073C3',
                            cursor: readWorkflows ? 'pointer' : 'not-allowed',
                            pointerEvents: 'auto',
                            textDecoration: 'none',
                            width: '50%',
                            opacity: readWorkflows ? 1 : 0.5
                          }}
                          // @ts-ignore
                          disabled={!readWorkflows}
                        >
                          <MUITypography
                            variant="body1"
                            sx={{
                              color: '#5073C3',
                              cursor: readWorkflows ? 'pointer' : 'not-allowed',
                              textDecoration: 'underline'
                            }}
                          >
                            Next task
                          </MUITypography>
                        </Link>
                      </LinkTooltip>
                    ) : (
                      <MUITypography
                        variant="body1"
                        sx={{
                          color: 'text.primary',
                          textAlign: 'right'
                        }}
                      >
                        Workflow complete
                      </MUITypography>
                    )}
                  </MUIStack>
                </div>
              )
            })
          ) : (
            <div className={styles.card} key="no-data-default" data-testid="emptySlide">
              <div className={`${styles.pieChartWrapper} ${styles.noWorkflowPieChartWrapper}`}>
                <PieChart
                  key="no-data-default"
                  colors={['#5073C3']}
                  series={[
                    {
                      data: [{ value: 10 }, { value: 10 }, { value: 10 }],
                      innerRadius: 30,
                      outerRadius: 40,
                      paddingAngle: 3,
                      cornerRadius: 25,
                      startAngle: 90,
                      endAngle: 180,
                      cx: '120%',
                      cy: '50%'
                    }
                  ]}
                  width={175}
                  height={116}
                  tooltip={{ trigger: 'none' }}
                  sx={{
                    [`& .${pieArcClasses.root}`]: {
                      cursor: 'pointer'
                    }
                  }}
                />
                <div className={styles.noDataIcon}></div>
              </div>
              <MUITypography
                variant="h6"
                data-testid="no-data-message"
                sx={{
                  padding: '0 0 0 4px',
                  margin: '0 auto',
                  textWrap: 'wrap',
                  width: '100%',
                  wordBreak: 'break-word',
                  textAlign: 'center'
                }}
              >
                No workflow loaded
              </MUITypography>
              {/* @ts-ignore */}
              <div variant="body1" className={styles.description}>
                <Link
                  className={styles.link}
                  to={`/case-management/matters/workflows/load?client_no=${clientNo}&matter_no=${matterNo}`}
                  style={{
                    color: '#5073C3',
                    cursor: 'pointer',
                    textDecoration: 'none',
                    width: '50%'
                  }}
                >
                  <MUITypography
                    variant="body1"
                    sx={{
                      color: '#5073C3',
                      cursor: 'pointer',
                      textDecoration: 'underline'
                    }}
                  >
                    Load workflow
                  </MUITypography>
                </Link>
              </div>
            </div>
          )}
        </Carousel>

        {totalSlides > 1 && (
          <div
            style={{
              position: 'absolute',
              top: '1rem',
              right: '1rem',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <IconButton onClick={handlePrev} sx={{ p: 0 }} data-testid="prevButton">
              <ChevronRightIcon sx={{ transform: 'rotate(180deg)' }} />
            </IconButton>
            <MUITypography
              data-testid="slideTracker"
              sx={{ fontWeight: 500, color: '#3D517B', marginBottom: '0.15rem' }}
            >
              {activeIndex + 1}/{totalSlides}
            </MUITypography>
            <IconButton onClick={handleNext} sx={{ p: 0 }} data-testid="nextButton">
              <ChevronRightIcon />
            </IconButton>
          </div>
        )}
      </MUIPaper>
    </div>
  )
}

export type WorkflowProgressCardProps = {
  sx?: SxProps
  testData?: {
    workflowId: number
    description: string
    workTypeId: string
    matterWorkflowWorkTypeTasks: {
      taskDescription: string
      matterWorkflowWorkTypeTaskId: number
      clientNo: string
      matterNo: number
      positionIndex: number
      workflowId: number
      matterWorkflowWorkTypeIdCount: number
      workTypeTaskId: number
      completedDate: string | null
      indentation: number
      workflowSectionId: number | null
      workflowSectionPositionIndex: number | null
    }
  }[]
}

WorkflowProgressCard.propTypes = {
  sx: PropTypes.object,
  testData: PropTypes.array
}
